import React from 'react';
import { Helmet } from 'react-helmet-async';
import Button from './Button';
import ServiceShowcase from './ServiceShowcase';
import { BRAND, brandify } from '../brandConfig';

const MicroservicesPage = ({ navigate }) => {
  return (
    <div className="py-32 max-w-[95vw] mx-auto px-4 md:px-8 bg-background min-h-screen flex flex-col justify-between">
      <Helmet>
        <title>{`Custom Microservices Development | ${BRAND}`}</title>
        <meta name="description" content={brandify('Wind Marketing engineers custom microservices and backend API integrations that scale independently, eliminate bottlenecks, and power modern digital operations for growing businesses.')} />
        <meta name="keywords" content={`microservices development, custom API development, backend engineering, scalable architecture, ${BRAND} microservices`} />
        <link rel="canonical" href="https://www.wind-marketing.com/microservices" />
        <meta property="og:title" content={`Custom Microservices Development • ${BRAND}`} />
        <meta property="og:description" content={brandify('Wind Marketing engineers custom microservices and backend APIs that scale independently and eliminate operational bottlenecks.')} />
        <meta property="og:url" content="https://www.wind-marketing.com/microservices" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Custom Microservices Development",
            "provider": {
              "@type": "Organization",
              "name": "${BRAND}",
              "url": "https://www.wind-marketing.com"
            },
            "description": "${brandify('Custom backend microservices, API integrations, and payment systems engineered for scale. Wind Marketing breaks monolithic bottlenecks into discrete, high-performance services that scale independently and communicate seamlessly.')}",
            "url": "https://www.wind-marketing.com/microservices",
            "serviceType": "Software Development",
            "areaServed": "Worldwide"
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What are microservices and why does my business need them?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Microservices break your digital operations into discrete, independent components that can scale and update individually. Unlike monolithic systems where one failure can take everything offline, microservices are resilient and allow your business to grow specific capabilities without rebuilding your entire tech stack."
                }
              },
              {
                "@type": "Question",
                "name": "Does ${BRAND} build payment processing systems?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "${brandify('Yes. Wind Marketing builds custom payment processing integrations using platforms like Stripe, including subscription billing, one-time payments, and checkout flows tailored to your specific business model.')}"
                }
              }
            ]
          }
        `}</script>
      </Helmet>
      {/* Main Content Area */}
      <div className="max-w-4xl mx-auto mt-12 mb-20 flex-grow w-full">
        {/* Breadcrumb / Back button */}


        {/* Header Section */}
        <div className="w-full mb-27">
          <h1 
            className="text-4.5xl whitespace-nowrap sm:text-6xl md:text-8xl lg:text-9xl font-bold uppercase tracking-tighter leading-none mb-8 text-foreground"
            style={{ fontFamily: "'Poppins Black', sans-serif" }}
          >
            MICROSERVICES
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-medium">
            Monolithic systems are the bottlenecks of modern enterprise. When one piece breaks, everything halts. Microservices break your digital operations down into discrete, independent, high-performance engines that communicate seamlessly and scale individually.
          </p>
        </div>

        <ServiceShowcase currentPath="/microservices" navigate={navigate} />

        {/* Interactive Buttons */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-12">
          <Button
            size="large"
            onClick={() => {
              navigate('/pricing');
              setTimeout(() => {
                document.getElementById('microservices-pricing')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
          >
            Engineer Your Microservices
          </Button>
          <Button 
            variant="outline" 
            size="large"
            onClick={() => navigate('/work')}
          >
            OUR SERVICES
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MicroservicesPage;
